// Toast line — top-center transient text chips. Replaces the scaffold's
// #status-line as the surface for connection status and wire events: the
// integrator pushes strings via MobileHudApi.showToast(). Purely passive
// (pointer-events stay off); newest toast appends at the bottom of a short
// stack, each fades out after its ttl.

const DEFAULT_TTL_MS = 2600;
const FADE_MS = 200;
const MAX_STACK = 4;

export interface Toasts {
  readonly el: HTMLElement;
  show(text: string, ms?: number): void;
  /** Cancel pending timers and clear the stack. */
  dispose(): void;
}

export function createToasts(): Toasts {
  const el = document.createElement('div');
  el.className = 'm3k-toasts';
  const timers = new Set<ReturnType<typeof setTimeout>>();

  function later(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
  }

  function show(text: string, ms = DEFAULT_TTL_MS): void {
    const toast = document.createElement('div');
    toast.className = 'm3k-toast';
    toast.textContent = text;
    el.appendChild(toast);
    while (el.children.length > MAX_STACK) el.firstElementChild?.remove();
    // Two-phase class add so the fade-in transition actually runs.
    requestAnimationFrame(() => toast.classList.add('m3k-toast--in'));
    later(() => {
      toast.classList.add('m3k-toast--out');
      later(() => toast.remove(), FADE_MS);
    }, ms);
  }

  return {
    el,
    show,
    dispose() {
      for (const id of timers) clearTimeout(id);
      timers.clear();
      el.replaceChildren();
    },
  };
}
